import React, { useState, useEffect, useRef, useMemo } from "react"
import { renderToStaticMarkup } from "react-dom/server"
import moment from "moment"
import AppApi from "../utils/AppApi"
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  LayersControl,
  LayerGroup,
  useMap,
  ZoomControl,
} from "react-leaflet"
import { divIcon } from "leaflet"
import ReactLeafletDriftMarker from "react-leaflet-drift-marker"

import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch"
import "leaflet-geosearch/dist/geosearch.css"

import icon from "../components/constants"

const LeafletgeoSearch = () => {
  const map = useMap()
  useEffect(() => {
    //const provider = new EsriProvider()
    const provider = new OpenStreetMapProvider({
      params: {
        "accept-language": "th",
        countrycodes: "th",
        addressdetails: 1, // include additional address detail parts
      },
    })

    const searchControl = new GeoSearchControl({
      provider,
      marker: {
        icon,
      },
    })

    map.addControl(searchControl)

    return () => map.removeControl(searchControl)
  }, [map])

  return null
}

const MapInstanceSetter = ({ mapRef }) => {
  const map = useMap()
  useEffect(() => {
    mapRef.current = map
  }, [map, mapRef])
  return null
}

const MapView = () => {
  const [trucks, setTrucks] = useState([])
  const [sinoTrucks, setSinoTrucks] = useState([])
  const [renderTrucks, setRenderTrcuk] = useState([])
  const [poi, setPoi] = useState([])
  const [searchText, setSearchText] = useState("")
  const [selectedTruck, setSelectedTruck] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [radarTileUrl, setRadarTileUrl] = useState(null)

  const trucksRef = useRef(trucks)
  const mapRef = useRef(null)
  const isFlyingRef = useRef(false)

  useEffect(() => {
    trucksRef.current = trucks
  })

  // Fetch RainViewer radar data
  useEffect(() => {
    const fetchRadar = async () => {
      try {
        const res = await fetch(
          "https://api.rainviewer.com/public/weather-maps.json",
        )
        const data = await res.json()
        const lastFrame = data.radar.past[data.radar.past.length - 1]
        setRadarTileUrl(
          `${data.host}${lastFrame.path}/256/{z}/{x}/{y}/2/1_1.png`,
        )
      } catch (e) {
        console.log("RainViewer fetch error:", e.message)
      }
    }
    fetchRadar()
    const iv = setInterval(fetchRadar, 10 * 60 * 1000) // refresh every 10 min
    return () => clearInterval(iv)
  }, [])

  useEffect(() => {
    const getTrucks = async () => {
      try {
        let response = await AppApi.get("/truck_location")

        setTrucks(response.data)

        setRenderTrcuk(response.data)
      } catch (error) {
        console.log(error.message)
      }
    }

    const getSinoTrucks = async () => {
      let sino_res = await AppApi.get("/sinotrack")
      setSinoTrucks(sino_res.data)
    }

    getTrucks()
    getSinoTrucks()
    const interval = setInterval(() => {
      getTrucks()
    }, 60000)
    const iv2 = setInterval(() => {
      getSinoTrucks()
    }, 10000)

    return () => {
      clearInterval(interval)
      clearInterval(iv2)
    }
  }, [])

  useEffect(() => {
    const getPoi = async () => {
      let start_date = moment().subtract(3, "days").format("YYMMDD")

      try {
        let response = await AppApi.post("/poi_dn", {
          start_date,
        })

        let final_poi = []

        response.data.forEach((poi) => {
          let dataIndex = final_poi.findIndex((d) => d._id === poi._id)

          //console.log('index', dataIndex)

          if (dataIndex === -1) {
            final_poi.push(poi)
          } else {
            //console.log(poi._id, final_poi[dataIndex])
            final_poi[dataIndex].dn = [...final_poi[dataIndex].dn, ...poi.dn]
          }
        })

        setPoi(final_poi)
      } catch (error) {
        console.log(error.message)
      }
    }

    return () => getPoi()
    // const interval2 = setInterval(() => {
    //   getPoi()
    // }, 60000)

    // return () => clearInterval(interval2)
  }, [trucks])

  useEffect(() => {
    const destinationPoint = (lat, lon, distance, bearing) => {
      var radius = 6371e3 // (Mean) radius of earth

      var toRadians = function (v) {
        return (v * Math.PI) / 180
      }
      var toDegrees = function (v) {
        return (v * 180) / Math.PI
      }

      // sinφ2 = sinφ1·cosδ + cosφ1·sinδ·cosθ
      // tanΔλ = sinθ·sinδ·cosφ1 / cosδ−sinφ1·sinφ2
      // see mathforum.org/library/drmath/view/52049.html for derivation

      var δ = Number(distance) / radius // angular distance in radians
      var θ = toRadians(Number(bearing))

      var φ1 = toRadians(Number(lat))
      var λ1 = toRadians(Number(lon))

      var sinφ1 = Math.sin(φ1),
        cosφ1 = Math.cos(φ1)
      var sinδ = Math.sin(δ),
        cosδ = Math.cos(δ)
      var sinθ = Math.sin(θ),
        cosθ = Math.cos(θ)

      var sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * cosθ
      var φ2 = Math.asin(sinφ2)
      var y = sinθ * sinδ * cosφ1
      var x = cosδ - sinφ1 * sinφ2
      var λ2 = λ1 + Math.atan2(y, x)

      return [toDegrees(φ2), ((toDegrees(λ2) + 540) % 360) - 180] // normalise to −180..+180°
    }

    // Snap พิกัดไปยังถนนที่ใกล้ที่สุดผ่าน OSRM nearest API
    const snapToRoad = async (lat, lng) => {
      try {
        const res = await fetch(
          `https://osrm.svlt.co.th/nearest/v1/driving/${lng},${lat}?number=1`,
        )
        const data = await res.json()
        if (data.code === "Ok" && data.waypoints?.length > 0) {
          const loc = data.waypoints[0].location
          return [loc[1], loc[0]] // OSRM returns [lng, lat] → convert to [lat, lng]
        }
      } catch (e) {
        console.log("snapToRoad error:", e.message)
      }
      return [lat, lng] // fallback ใช้พิกัดเดิม
    }

    console.log("Get New Trcuks Data")
    var snapshot_trucks = [...trucks]
    let tickCount = 0
    let isSnapping = false

    const iv = setInterval(() => {
      let updated_trucks = []
      tickCount++

      console.log("snapshot_trucks", snapshot_trucks.length)

      snapshot_trucks.forEach((truck) => {
        let lat = truck.latitude
        let lng = truck.longitude
        let distance = (truck.speed * 0.8 * 1000) / 3600

        let new_location = destinationPoint(lat, lng, distance, truck.direction)

        updated_trucks.push({
          ...truck,
          latitude: new_location[0],
          longitude: new_location[1],
        })
      })

      setRenderTrcuk(updated_trucks)
      snapshot_trucks = [...updated_trucks]

      // ทุก 5 วินาที snap พิกัดรถที่กำลังวิ่งไปยังถนนที่ใกล้ที่สุด
      // เพื่อแก้ปัญหาพิกัดที่คำนวณหลุดออกจากถนน
      // snap เฉพาะรถที่อยู่ในขอบเขตแผนที่ที่แสดงผลบนหน้าจอเท่านั้น
      // และ zoom >= 14 เท่านั้น (zoom ต่ำกว่านี้มองไม่เห็นว่าหลุดถนน)
      const currentZoom = mapRef.current?.getZoom() || 0
      if (tickCount % 5 === 0 && !isSnapping && currentZoom >= 14) {
        isSnapping = true
        const mapBounds = mapRef.current?.getBounds()
        Promise.all(
          updated_trucks.map(async (truck) => {
            // ไม่ต้อง snap รถที่จอดนิ่ง
            if (truck.speed <= 0) return truck
            // ไม่ต้อง snap รถที่อยู่นอกหน้าจอ
            if (
              mapBounds &&
              !mapBounds.contains([truck.latitude, truck.longitude])
            )
              return truck
            const [lat, lng] = await snapToRoad(truck.latitude, truck.longitude)
            return { ...truck, latitude: lat, longitude: lng }
          }),
        )
          .then((snappedTrucks) => {
            snapshot_trucks = [...snappedTrucks]
            setRenderTrcuk(snappedTrucks)
            isSnapping = false
          })
          .catch(() => {
            isSnapping = false
          })
      }
    }, 1000)

    return () => clearInterval(iv)
  }, [trucks])

  // const customMarkerIcon = divIcon({
  //   html: renderToStaticMarkup(<i className="fas fa-truck fa-2x" />),
  // })

  var pv_date = ""

  const getTruckStatus = (truck) => {
    if (truck.evEngine === 0)
      return { label: "ดับเครื่อง", color: "#f44336", bg: "#ffebee" }
    if (truck.speed > 0)
      return { label: `${truck.speed} km/h`, color: "#4caf50", bg: "#e8f5e9" }
    return { label: "จอดนิ่ง", color: "#ff9800", bg: "#fff3e0" }
  }

  const filteredTrucks = useMemo(() => {
    if (!searchText.trim()) return renderTrucks
    const q = searchText.trim().toLowerCase()
    return renderTrucks.filter((t) => t.vehicleName?.toLowerCase().includes(q))
  }, [renderTrucks, searchText])

  // invalidate map size เมื่อ sidebar เปิด/ปิด เพื่อให้แผนที่แสดงเต็มจอ
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize()
      }
    }, 350) // รอให้ CSS transition (0.3s) เสร็จก่อน
    return () => clearTimeout(timer)
  }, [sidebarOpen])

  const handleTruckClick = (truck) => {
    if (selectedTruck === truck.vehicleName) {
      setSelectedTruck(null)
      return
    }
    setSelectedTruck(truck.vehicleName)
    if (mapRef.current) {
      isFlyingRef.current = true
      mapRef.current.flyTo([truck.latitude, truck.longitude], 16, {
        duration: 1,
      })
      mapRef.current.once("moveend", () => {
        isFlyingRef.current = false
      })
    }
  }

  // เลื่อนแผนที่ตามรถที่เลือกเมื่อพิกัดเปลี่ยน
  useEffect(() => {
    if (!selectedTruck || !mapRef.current || isFlyingRef.current) return
    const truck = renderTrucks.find((t) => t.vehicleName === selectedTruck)
    if (truck) {
      mapRef.current.setView(
        [truck.latitude, truck.longitude],
        mapRef.current.getZoom(),
        {
          animate: true,
          duration: 0.5,
        },
      )
    }
  }, [renderTrucks, selectedTruck])

  return (
    <div style={{ display: "flex", height: "95vh", position: "relative" }}>
      {/* Toggle Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        style={{
          position: "absolute",
          left: sidebarOpen ? 310 : 0,
          top: 80,
          zIndex: 1000,
          background: "#1976d2",
          color: "#fff",
          border: "none",
          borderRadius: sidebarOpen ? "0 4px 4px 0" : "0 4px 4px 0",
          padding: "8px 6px",
          cursor: "pointer",
          fontSize: "14px",
          boxShadow: "2px 2px 6px rgba(0,0,0,0.3)",
          transition: "left 0.3s ease",
        }}
      >
        <i className={`fas fa-chevron-${sidebarOpen ? "left" : "right"}`} />
      </button>

      {/* Sidebar */}
      <div
        style={{
          width: sidebarOpen ? 320 : 0,
          minWidth: sidebarOpen ? 320 : 0,
          transition: "width 0.3s ease, min-width 0.3s ease",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRight: sidebarOpen ? "1px solid #e0e0e0" : "none",
          background: "#fafafa",
        }}
      >
        {/* Sidebar Header */}
        <div
          style={{
            padding: "12px",
            borderBottom: "1px solid #e0e0e0",
            background: "#1976d2",
            color: "#fff",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: "14px", marginBottom: 6 }}>
            <i className="fas fa-truck" style={{ marginRight: 6 }} />
            รายการรถ ({renderTrucks.length})
          </div>
          <input
            type="text"
            placeholder="ค้นหาทะเบียนรถ..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 10px",
              borderRadius: 4,
              border: "none",
              fontSize: "13px",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Truck List */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {filteredTrucks
            .sort((a, b) =>
              (a.vehicleName || "").localeCompare(b.vehicleName || ""),
            )
            .map((t) => {
              const status = getTruckStatus(t)
              const dn = t.lastest_dn?.[0]
              const isSelected = selectedTruck === t.vehicleName
              return (
                <div
                  key={t.vehicleName}
                  onClick={() => handleTruckClick(t)}
                  style={{
                    padding: "8px 12px",
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    background: isSelected ? "#e3f2fd" : "#fff",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "#f5f5f5"
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.background = "#fff"
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 2,
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: "13px" }}>
                      {t.vehicleName}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: status.color,
                        background: status.bg,
                        padding: "2px 8px",
                        borderRadius: 10,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {status.label}
                    </span>
                  </div>
                  {dn && (
                    <div style={{ fontSize: "11px", color: "#888" }}>
                      <span
                        style={{
                          color:
                            dn.status === "discharged" ? "#4caf50" : "#f44336",
                          fontWeight: 500,
                        }}
                      >
                        {dn.status === "discharged"
                          ? "ว่าง"
                          : dn.status?.toUpperCase()}
                      </span>
                      {" • "}
                      {dn.loading_point}
                      {dn.discharges?.map((d) => ` → ${d.discharge_point}`)}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#aaa",
                      marginTop: 2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t.address || "-"}
                  </div>
                </div>
              )
            })}
          {filteredTrucks.length === 0 && (
            <div
              style={{
                padding: 20,
                textAlign: "center",
                color: "#999",
                fontSize: "13px",
              }}
            >
              ไม่พบข้อมูล
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <div style={{ flex: 1 }}>
        <MapContainer
          center={[13.638468, 100.557651]}
          zoom={12}
          zoomControl={false}
          style={{ height: "95vh" }}
        >
          <MapInstanceSetter mapRef={mapRef} />
          <ZoomControl position="bottomright" />
          <LeafletgeoSearch />
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="OpenStreetMap.Mapnik">
              <TileLayer
                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Esri.WorldImagery">
              <TileLayer
                maxZoom={18}
                attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Google.Map">
              <TileLayer
                maxZoom={20}
                subdomains={["mt0", "mt1", "mt2", "mt3"]}
                attribution="Tiles &copy; Google Map"
                url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Google.Hybrid">
              <TileLayer
                maxZoom={20}
                subdomains={["mt0", "mt1", "mt2", "mt3"]}
                attribution="Tiles &copy; Google Map"
                url="http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
              />
            </LayersControl.BaseLayer>
            <LayersControl.Overlay checked name="Loading Poi">
              <LayerGroup>
                {poi.map((p) => {
                  //console.log(p._id)
                  const icon = divIcon({
                    html: renderToStaticMarkup(
                      <div>
                        <i
                          className="fas fa-map-marker-alt fa-2x"
                          style={{
                            color: "blue",
                            textShadow: "-1px -1px 0px black",
                          }}
                        ></i>
                        <div className="marker_label">
                          <span>{p._id}</span>
                        </div>
                      </div>,
                    ),
                  })

                  if (p.lat === null || p.lng === null) {
                    return null
                  }

                  return (
                    <Marker position={[p.lat, p.lng]} icon={icon} key={p._id}>
                      <Popup>
                        <b>({p._id})</b> {p.name}
                        <br />
                        {p.address}
                        <br />
                        {p.lat},{p.lng}
                        <hr />
                        <div className="popup_list">
                          {p.dn.length > 0
                            ? p.dn
                                .sort((a, b) => b.dn - a.dn)
                                .map((d, i) => {
                                  //ถ้าเป็น Record แรกไม่ต้องขีดเส้น
                                  if (i === 0) {
                                    pv_date = d.dn.substring(0, 6)
                                    return (
                                      <>
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            gap: "4px",
                                          }}
                                        >
                                          <div>{d.dn}</div>
                                          <div style={{ minWidth: "30px" }}>
                                            {d.truck_no}
                                          </div>
                                          <div style={{ flex: "2 1" }}>
                                            {`${d.loading_point} `}
                                            <i className="fas fa-arrow-right" />
                                            {Array.isArray(d.discharges) &&
                                              d.discharges.map(
                                                (dis) =>
                                                  ` ${dis.discharge_point} `,
                                              )}
                                            {!Array.isArray(d.discharges) &&
                                              ` ${d.discharges.discharge_point} `}
                                          </div>
                                          <div>
                                            {" "}
                                            <span
                                              style={{
                                                color:
                                                  d.status === "discharged"
                                                    ? "green"
                                                    : "red",
                                              }}
                                            >
                                              {d.status === "discharged" ? (
                                                <i className="fas fa-check"></i>
                                              ) : (
                                                String(d.status).toUpperCase()
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      </>
                                    )
                                  }

                                  if (pv_date === d.dn.substring(0, 6)) {
                                    pv_date = d.dn.substring(0, 6)
                                    return (
                                      <>
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                            flexDirection: "row",
                                          }}
                                        >
                                          <div>{d.dn}</div>
                                          <div style={{ minWidth: "30px" }}>
                                            {d.truck_no}
                                          </div>
                                          <div style={{ flex: "2 1" }}>
                                            {`${d.loading_point} `}
                                            <i className="fas fa-arrow-right" />
                                            {Array.isArray(d.discharges) &&
                                              d.discharges.map(
                                                (dis) =>
                                                  ` ${dis.discharge_point} `,
                                              )}
                                            {!Array.isArray(d.discharges) &&
                                              ` ${d.discharges.discharge_point} `}
                                          </div>
                                          <div>
                                            {" "}
                                            <span
                                              style={{
                                                color:
                                                  d.status === "discharged"
                                                    ? "green"
                                                    : "red",
                                              }}
                                            >
                                              {d.status === "discharged" ? (
                                                <i className="fas fa-check"></i>
                                              ) : (
                                                String(d.status).toUpperCase()
                                              )}
                                            </span>
                                          </div>
                                        </div>
                                      </>
                                    )
                                  } else {
                                    pv_date = d.dn.substring(0, 6)
                                    return (
                                      <React.Fragment>
                                        <hr />
                                        <>
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "4px",
                                              flexDirection: "row",
                                            }}
                                          >
                                            <div>{d.dn}</div>
                                            <div style={{ minWidth: "30px" }}>
                                              {d.truck_no}
                                            </div>
                                            <div style={{ flex: "2 1" }}>
                                              {`${d.loading_point} `}
                                              <i className="fas fa-arrow-right" />
                                              {Array.isArray(d.discharges) &&
                                                d.discharges.map(
                                                  (dis) =>
                                                    ` ${dis.discharge_point} `,
                                                )}
                                              {!Array.isArray(d.discharges) &&
                                                ` ${d.discharges.discharge_point} `}
                                            </div>
                                            <div>
                                              {" "}
                                              <span
                                                style={{
                                                  color:
                                                    d.status === "discharged"
                                                      ? "green"
                                                      : "red",
                                                }}
                                              >
                                                {d.status === "discharged" ? (
                                                  <i className="fas fa-check"></i>
                                                ) : (
                                                  String(d.status).toUpperCase()
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                        </>
                                      </React.Fragment>
                                    )
                                  }
                                })
                            : null}
                        </div>
                      </Popup>
                    </Marker>
                  )
                })}
              </LayerGroup>
            </LayersControl.Overlay>
            <LayersControl.Overlay checked name="Truck">
              <LayerGroup>
                {renderTrucks.map((t) => {
                  let dn_list = [...(t.lastest_dn || [])]
                  let dn_list_rev = [...(t.lastest_dn || [])].reverse()
                  if (dn_list.length === 0) return null
                  let isShowRoute =
                    dn_list[0].status !== "discharged" ||
                    parseInt(dn_list[0].dn.substring(0, 6)) >=
                      parseInt(moment().subtract(3, "days").format("YYMMDD"))
                  let truck_icon =
                    dn_list[0].status === "discharged"
                      ? "fas fa-truck"
                      : "fas fa-shipping-fast"
                  let icon = divIcon({
                    html: renderToStaticMarkup(
                      <div
                        key={t.vehicleName}
                        style={{
                          color:
                            t.evEngine === 0
                              ? "red"
                              : t.speed > 0
                                ? "green"
                                : "orange",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "16px",

                            textShadow: "-1px -1px 3px black",
                          }}
                        >
                          {t.direction > 180 ? (
                            <i
                              className={truck_icon}
                              style={{
                                transform: `rotate(${
                                  t.direction - 90
                                }deg) scaleY(-1)`,
                              }}
                            />
                          ) : (
                            <i
                              className={truck_icon}
                              style={{
                                transform: `rotate(${t.direction - 90}deg)`,
                              }}
                            />
                          )}
                        </div>

                        <div className="marker_label">
                          <div>{t.vehicleName}</div>

                          {isShowRoute && (
                            <div
                              style={{ fontSize: "0.6rem", fontWeight: 600 }}
                            >
                              {
                                dn_list_rev[dn_list_rev.length - 1]
                                  .loading_point
                              }
                              {dn_list_rev[
                                dn_list_rev.length - 1
                              ].discharges.map((d) => (
                                <span key={d.discharge_point}>
                                  {" "}
                                  - {d.discharge_point}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>,
                    ),
                  })
                  return (
                    <ReactLeafletDriftMarker
                      position={[t.latitude, t.longitude]}
                      icon={icon}
                      duration={1000}
                      key={t.vehicleName}
                    >
                      <Popup>
                        <div>
                          <b>
                            {t.vehicleName} - {t.speed} km/h
                          </b>
                        </div>
                        <div>
                          {dn_list[0].driver} - {dn_list[0].tel}
                        </div>
                        <div style={{ maxWidth: "500px" }}>{t.address}</div>
                        <div>
                          {t.latitude},{t.longitude}
                        </div>

                        <hr />
                        {dn_list_rev.map((d) => (
                          <>
                            <div
                              style={{
                                flexDirection: "row",
                                display: "flex", // Use flexbox
                                justifyContent: "space-between", // Distribute items evenly with space between
                              }}
                            >
                              <div style={{ flex: "1 1 " }}>{d.dn}</div>
                              <div style={{ flex: "2 1 " }}>
                                {`${d.loading_point} `}
                                <i className="fas fa-arrow-right" />
                                {` ${d.discharges.map(
                                  (dis) => dis.discharge_point,
                                )} `}
                              </div>
                              <div style={{ flex: "1 1 " }}>
                                <span
                                  style={{
                                    color:
                                      d.status === "discharged"
                                        ? "green"
                                        : "red",
                                  }}
                                >
                                  {d.status === "discharged" ? (
                                    <i className="fas fa-check"></i>
                                  ) : (
                                    String(d.status).toUpperCase()
                                  )}
                                </span>
                              </div>
                            </div>
                          </>
                        ))}
                      </Popup>
                    </ReactLeafletDriftMarker>
                  )
                })}
              </LayerGroup>
            </LayersControl.Overlay>
            <LayersControl.Overlay name="Sinotrack">
              <LayerGroup>
                {sinoTrucks.map((t) => {
                  let truck_icon =
                    [...t.lastest_dn][0]?.status === "discharged"
                      ? "fas fa-truck"
                      : "fas fa-shipping-fast"
                  let dn_list_rev = [...t.lastest_dn].reverse()

                  let icon = divIcon({
                    html: renderToStaticMarkup(
                      <div
                        key={t.strTEID}
                        style={{
                          color: "#FF00FF",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "16px",

                            textShadow: "-1px -1px 3px black",
                          }}
                        >
                          {t.nDirection > 180 ? (
                            <i
                              className={truck_icon}
                              style={{
                                transform: `rotate(${
                                  t.nDirection - 90
                                }deg) scaleY(-1)`,
                              }}
                            />
                          ) : (
                            <i
                              className={truck_icon}
                              style={{
                                transform: `rotate(${t.nDirection - 90}deg)`,
                              }}
                            />
                          )}
                        </div>

                        <div className="marker_label">
                          <div>
                            {t.strTEID} <br /> BT:{" "}
                            {parseInt(t.nTEState / 65535)} SP:{t.nSpeed} <br />{" "}
                            {dn_list_rev[dn_list_rev.length - 1]?.license_plate}
                          </div>
                          <div style={{ fontSize: "0.6rem", fontWeight: 600 }}>
                            {dn_list_rev[dn_list_rev.length - 1]?.loading_point}
                            {dn_list_rev[
                              dn_list_rev.length - 1
                            ]?.discharges.map((d) => (
                              <span key={d.discharge_point}>
                                {" "}
                                - {d.discharge_point}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>,
                    ),
                  })
                  return (
                    <ReactLeafletDriftMarker
                      position={[t.dbLat, t.dbLon]}
                      icon={icon}
                      duration={10000}
                      key={t.strTEID}
                    >
                      <Popup>
                        <div>
                          <b>
                            {t.strTEID} - {t.nSpeed} km/h
                          </b>
                        </div>
                        <div>{[...t.lastest_dn][0]?.license_plate}</div>
                        <div>
                          {[...t.lastest_dn][0]?.driver} -{" "}
                          {[...t.lastest_dn][0]?.tel}
                        </div>
                        <div>Batt: {parseInt(t.nTEState / 65535)} %</div>
                        <hr />
                        {[...t.lastest_dn].reverse().map((d) => (
                          <li key={d.dn}>
                            {d.dn} - {d.loading_point}{" "}
                            <i className="fas fa-arrow-right"></i>{" "}
                            {d.discharges.map(
                              (dis) => dis.discharge_point + " ",
                            )}{" "}
                            -{" "}
                            <span
                              style={{
                                color:
                                  d.status === "discharged" ? "green" : "red",
                              }}
                            >
                              {d.status}
                            </span>
                          </li>
                        ))}
                      </Popup>
                    </ReactLeafletDriftMarker>
                  )
                })}
              </LayerGroup>
            </LayersControl.Overlay>
            {radarTileUrl && (
              <LayersControl.Overlay checked name="Rain Radar">
                <TileLayer
                  url={radarTileUrl}
                  opacity={0.5}
                  zIndex={10}
                  maxNativeZoom={7}
                  maxZoom={20}
                />
              </LayersControl.Overlay>
            )}
          </LayersControl>
        </MapContainer>
      </div>
    </div>
  )
}

export default MapView
